import { RoadmapPhase, AutoValidationContext } from '../types/roadmap';

export const ROADMAP_WORKFLOW: RoadmapPhase[] = [
  {
    key: 'diagnostic',
    label: 'Diagnostic initial & positionnement',
    description: 'Analyse de la situation et définition du projet ESS',
    icon: 'Compass',
    color: 'blue',
    steps: [
      {
        key: 'entretien_decouverte',
        label: 'Entretien découverte',
        description: 'Premier rendez-vous avec le porteur pour comprendre le projet',
        autoValidationCondition: (context) => {
          return context.appointments.some(
            apt => apt.status === 'TERMINE' || apt.status === 'CONFIRME'
          );
        }
      },
      {
        key: 'diagnostic_projet',
        label: 'Diagnostic projet',
        description: 'Analyse approfondie du projet et de son environnement',
        requiresMessages: true,
        autoValidationCondition: (context) => {
          return context.messages.length >= 2 &&
                 context.documents.some(doc => doc.document_type === 'DESCRIPTION');
        }
      },
      {
        key: 'cartographie_acteurs',
        label: 'Cartographie des acteurs',
        description: 'Identification des partenaires et parties prenantes',
        autoValidationCondition: (context) => {
          return context.messages.filter(msg =>
            msg.subject.toLowerCase().includes('partenaire') ||
            msg.subject.toLowerCase().includes('acteur')
          ).length >= 1;
        }
      }
    ]
  },
  {
    key: 'structuration',
    label: 'Structuration du projet',
    description: 'Élaboration du modèle et structuration juridique',
    icon: 'Building2',
    color: 'green',
    steps: [
      {
        key: 'atelier_vision_impact',
        label: 'Atelier Vision & Impact',
        description: 'Définition de la vision et de l\'impact social attendu',
        requiresProjectStatus: ['SOUMIS', 'ACCORD_PROVISOIRE', 'VALIDE'],
        autoValidationCondition: (context) => {
          return context.project.status === 'SOUMIS' ||
                 context.project.status === 'ACCORD_PROVISOIRE' ||
                 context.project.status === 'VALIDE';
        }
      },
      {
        key: 'business_model',
        label: 'Business model',
        description: 'Construction du modèle économique ESS',
        requiredDocumentTypes: ['BUSINESS_PLAN'],
        autoValidationCondition: (context) => {
          return context.documents.some(
            doc => doc.document_type === 'BUSINESS_PLAN' && doc.is_validated
          );
        }
      },
      {
        key: 'plan_formation',
        label: 'Plan de formation',
        description: 'Identification des besoins en formation et accompagnement',
        autoValidationCondition: (context) => {
          return context.messages.filter(msg =>
            msg.subject.toLowerCase().includes('formation') ||
            msg.subject.toLowerCase().includes('compétence')
          ).length >= 1;
        }
      }
    ]
  },
  {
    key: 'ecosysteme',
    label: 'Mobilisation de l\'écosystème',
    description: 'Mise en réseau et partenariats stratégiques',
    icon: 'Network',
    color: 'purple',
    steps: [
      {
        key: 'mise_reseau',
        label: 'Mise en réseau',
        description: 'Connexion avec l\'écosystème ESS local et régional',
        autoValidationCondition: (context) => {
          return context.appointments.filter(apt =>
            apt.status === 'TERMINE'
          ).length >= 2;
        }
      },
      {
        key: 'challenge_projet',
        label: 'Challenge projet',
        description: 'Présentation du projet aux pairs et retours critiques',
        autoValidationCondition: (context) => {
          return context.messages.filter(msg =>
            msg.subject.toLowerCase().includes('feedback') ||
            msg.subject.toLowerCase().includes('retour') ||
            msg.subject.toLowerCase().includes('challenge')
          ).length >= 1;
        }
      },
      {
        key: 'ajustement_feedback',
        label: 'Ajustement post-feedback',
        description: 'Intégration des retours et ajustements du projet',
        autoValidationCondition: (context) => {
          const businessPlanDocs = context.documents.filter(
            doc => doc.document_type === 'BUSINESS_PLAN'
          );
          return businessPlanDocs.length >= 2;
        }
      }
    ]
  },
  {
    key: 'financement',
    label: 'Préparation au financement',
    description: 'Construction du dossier de financement et simulation',
    icon: 'DollarSign',
    color: 'amber',
    steps: [
      {
        key: 'revue_dossier',
        label: 'Revue du dossier',
        description: 'Vérification et consolidation du dossier de financement',
        requiredDocumentTypes: ['BUSINESS_PLAN', 'FINANCIAL_STATEMENT', 'LEGAL_DOCUMENT'],
        autoValidationCondition: (context) => {
          const hasBusinessPlan = context.documents.some(
            doc => doc.document_type === 'BUSINESS_PLAN' && doc.is_validated
          );
          const hasFinancial = context.documents.some(
            doc => doc.document_type === 'FINANCIAL_STATEMENT'
          );
          const hasLegal = context.documents.some(
            doc => doc.document_type === 'LEGAL_DOCUMENT'
          );
          return hasBusinessPlan && hasFinancial && hasLegal;
        }
      },
      {
        key: 'simulation_jury',
        label: 'Simulation jury',
        description: 'Préparation et simulation de la présentation devant le jury',
        autoValidationCondition: (context) => {
          return context.appointments.some(
            apt => (apt.status === 'TERMINE' || apt.status === 'CONFIRME') &&
            new Date(apt.start_date) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
          );
        }
      },
      {
        key: 'validation_cress',
        label: 'Validation interne CRESS',
        description: 'Validation finale du dossier par l\'équipe CRESS',
        requiresProjectStatus: ['VALIDE', 'DEBLOCAGE_FONDS'],
        autoValidationCondition: (context) => {
          return context.project.status === 'VALIDE' ||
                 context.project.status === 'DEBLOCAGE_FONDS';
        }
      }
    ]
  },
  {
    key: 'suivi',
    label: 'Suivi post-accompagnement',
    description: 'Accompagnement au démarrage et suivi d\'impact',
    icon: 'TrendingUp',
    color: 'emerald',
    steps: [
      {
        key: 'mise_oeuvre',
        label: 'Mise en œuvre',
        description: 'Suivi du lancement et des premières actions du projet',
        requiresProjectStatus: ['EN_COURS'],
        autoValidationCondition: (context) => {
          return context.project.status === 'EN_COURS';
        }
      },
      {
        key: 'suivi_impact',
        label: 'Suivi d\'impact',
        description: 'Mesure et évaluation de l\'impact social du projet',
        autoValidationCondition: (context) => {
          const projectAgeInMonths =
            (Date.now() - new Date(context.project.created_at).getTime()) /
            (1000 * 60 * 60 * 24 * 30);

          return projectAgeInMonths >= 6 &&
                 context.messages.filter(msg =>
                   msg.subject.toLowerCase().includes('impact') ||
                   msg.subject.toLowerCase().includes('résultat')
                 ).length >= 1;
        }
      },
      {
        key: 'rapport_final',
        label: 'Rapport final',
        description: 'Bilan de l\'accompagnement et clôture',
        requiresProjectStatus: ['TERMINE'],
        autoValidationCondition: (context) => {
          return context.project.status === 'TERMINE' &&
                 context.documents.some(
                   doc => doc.document_type === 'OTHER' &&
                   (doc as any).filename?.toLowerCase().includes('rapport')
                 );
        }
      }
    ]
  }
];

export function getPhaseByKey(key: string): RoadmapPhase | undefined {
  return ROADMAP_WORKFLOW.find(phase => phase.key === key);
}

export function getStepByKeys(phaseKey: string, stepKey: string) {
  const phase = getPhaseByKey(phaseKey);
  if (!phase) return undefined;
  return phase.steps.find(step => step.key === stepKey);
}

export function getTotalSteps(): number {
  return ROADMAP_WORKFLOW.reduce((total, phase) => total + phase.steps.length, 0);
}

export function getPhaseColor(phaseKey: string): string {
  const phase = getPhaseByKey(phaseKey);
  return phase?.color || 'gray';
}

export function getPhaseIcon(phaseKey: string): string {
  const phase = getPhaseByKey(phaseKey);
  return phase?.icon || 'Circle';
}
